import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    downloadFileByUrl,
    loadMetadata,
    loadSearchModuleDetail,
    loadSearchRequestOptions,
    updateSearchModule,
} from './api';
import { updateStoredSearchItem } from './searchState';
import SearchableSelect from './SearchableSelect';


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function normalizeRequestOption(item) {
    if (!item) {
        return null;
    }

    return {
        id: item.id,
        slug: item.slug,
        application_name: item.application_name || '',
        application_sending: item.application_sending || null,
        location_city: item.location_city || item.location?.city || '',
        related: item.related || '',
        whom: item.whom || '',
        transport: item.transport || '',
        ttn: item.ttn || '',
        flag_file_act: Boolean(item.flag_file_act),
    };
}


function formatRequestOptionTitle(item) {
    return item?.application_name || item?.slug || 'Без названия';
}


function formatRequestOptionMeta(item) {
    if (!item) {
        return '';
    }

    return [
        item.location_city,
        item.whom,
        item.related,
        item.application_sending ? formatDate(item.application_sending) : '',
    ].filter(Boolean).join(' • ');
}


function createEditDraft(item) {
    return {
        repair_status: item?.repair_status || '',
        movement_status: item?.movement_status || '',
        system: item?.system || '',
        engineer: item?.engineer || '',
        arrival_date: item?.arrival_date || '',
        date_repair: item?.date_repair || '',
        date_of_sending: item?.date_of_sending || '',
        request_slug: item?.request_send?.slug || '',
        comment: item?.comment || '',
    };
}


export default function SearchDetailPage() {
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [metadata, setMetadata] = useState({
        choices: {
            repair_statuses: [],
            movement_statuses: [],
            systems: [],
        },
    });
    const [moduleState, setModuleState] = useState({
        item: null,
        loading: true,
        error: '',
    });
    const [draft, setDraft] = useState(createEditDraft(null));
    const [saveState, setSaveState] = useState({
        saving: false,
        message: '',
        error: '',
    });
    const [downloadState, setDownloadState] = useState({
        excel: false,
        pdf: false,
    });
    const [requestQuery, setRequestQuery] = useState('');
    const [requestOptions, setRequestOptions] = useState([]);
    const [requestLookupLoading, setRequestLookupLoading] = useState(false);
    const [requestLookupError, setRequestLookupError] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [toast, setToast] = useState(null);

    const currentRequest = selectedRequest || normalizeRequestOption(moduleState.item?.request_send);
    const backTo = location.state?.backTo || '/app/search';
    const backLabel = location.state?.backLabel || 'Назад к поиску';
    const requestChanged = Boolean(
        moduleState.item
        && (currentRequest?.slug || '') !== (moduleState.item.request_send?.slug || ''),
    );
    const requestSelectOptions = useMemo(() => {
        const source = [selectedRequest, ...requestOptions].filter(Boolean);
        const seen = new Set();

        return source
            .filter((option) => {
                if (!option?.slug || seen.has(option.slug)) {
                    return false;
                }
                seen.add(option.slug);
                return true;
            })
            .map((option) => ({
                value: option.slug,
                label: formatRequestOptionTitle(option),
                meta: [option.slug, formatRequestOptionMeta(option)].filter(Boolean).join(' • '),
            }));
    }, [selectedRequest, requestOptions]);

    async function fetchRequestOptions(query = '', selectedSlug = '') {
        const payload = await loadSearchRequestOptions(query, selectedSlug);
        setRequestOptions((payload.items || []).map(normalizeRequestOption));
    }

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const [metadataPayload, modulePayload] = await Promise.all([
                    loadMetadata(),
                    loadSearchModuleDetail(moduleId),
                ]);

                if (cancelled) {
                    return;
                }

                setMetadata(metadataPayload);
                setModuleState({
                    item: modulePayload,
                    loading: false,
                    error: '',
                });
                setDraft(createEditDraft(modulePayload));
                setSelectedRequest(normalizeRequestOption(modulePayload.request_send));

                const requestOptionsPayload = await loadSearchRequestOptions('', modulePayload.request_send?.slug || '');
                if (!cancelled) {
                    setRequestOptions((requestOptionsPayload.items || []).map(normalizeRequestOption));
                }
            } catch (error) {
                if (!cancelled) {
                    setModuleState({
                        item: null,
                        loading: false,
                        error: extractErrorMessage(error, 'Не удалось загрузить данные модуля.'),
                    });
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [moduleId]);

    useEffect(() => {
        let cancelled = false;
        const trimmedQuery = requestQuery.trim();

        if (!trimmedQuery || trimmedQuery.length < 2) {
            setRequestLookupLoading(false);
            setRequestLookupError('');
            return undefined;
        }

        const timer = window.setTimeout(async () => {
            setRequestLookupLoading(true);
            setRequestLookupError('');

            try {
                const payload = await loadSearchRequestOptions(trimmedQuery, draft.request_slug || '');
                if (cancelled) {
                    return;
                }
                setRequestOptions((payload.items || []).map(normalizeRequestOption));
            } catch (error) {
                if (!cancelled) {
                    setRequestLookupError(extractErrorMessage(error, 'Не удалось найти заявки.'));
                    setRequestOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setRequestLookupLoading(false);
                }
            }
        }, 240);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [requestQuery, draft.request_slug]);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setToast(null);
        }, 2600);

        return () => window.clearTimeout(timer);
    }, [toast]);

    function updateDraft(name, value) {
        setDraft((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function selectRequest(option) {
        const normalized = normalizeRequestOption(option);
        setSelectedRequest(normalized);
        setRequestQuery('');
        setRequestLookupError('');
        setDraft((current) => ({
            ...current,
            request_slug: normalized?.slug || '',
        }));
    }

    function clearRequestBinding() {
        setSelectedRequest(null);
        setRequestQuery('');
        setRequestLookupError('');
        setDraft((current) => ({
            ...current,
            request_slug: '',
        }));
    }

    async function handleSave(event) {
        event.preventDefault();
        if (!moduleState.item) {
            return;
        }

        setSaveState({
            saving: true,
            message: '',
            error: '',
        });

        try {
            const payload = await updateSearchModule(moduleState.item.id, draft);
            setModuleState({
                item: payload.module,
                loading: false,
                error: '',
            });
            setDraft(createEditDraft(payload.module));
            setSelectedRequest(normalizeRequestOption(payload.module.request_send));
            setRequestQuery('');
            setRequestLookupError('');
            await fetchRequestOptions('', payload.module.request_send?.slug || '');
            updateStoredSearchItem(payload.module);
            setSaveState({
                saving: false,
                message: 'Данные модуля сохранены.',
                error: '',
            });
            setToast({
                type: 'success',
                text: 'Модуль сохранен.',
            });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось сохранить модуль.');
            setSaveState({
                saving: false,
                message: '',
                error: message,
            });
            setToast({
                type: 'error',
                text: message,
            });
        }
    }

    async function handleDownload(kind) {
        if (!moduleState.item) {
            return;
        }

        const fileUrl = kind === 'excel'
            ? moduleState.item.request_send?.excel_file_url
            : moduleState.item.request_send?.pdf_file_url;
        if (!fileUrl) {
            return;
        }

        setDownloadState((current) => ({
            ...current,
            [kind]: true,
        }));

        try {
            await downloadFileByUrl(fileUrl, `${moduleState.item.serial_number}-${kind}`);
        } catch (error) {
            setSaveState((current) => ({
                ...current,
                error: extractErrorMessage(error, 'Не удалось скачать файл.'),
            }));
        } finally {
            setDownloadState((current) => ({
                ...current,
                [kind]: false,
            }));
        }
    }

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}

            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Детали модуля</h3>
                    </div>

                    <div className="shell-actionCluster">
                        <button
                            type="button"
                            className="shell-actionButton shell-actionButton--secondary"
                            onClick={() => navigate(backTo, backTo === '/app/search' ? { state: { restoreSearch: true } } : undefined)}
                        >
                            {backLabel}
                        </button>
                    </div>
                </div>
            </section>

            {moduleState.error ? <div className="shell-banner">{moduleState.error}</div> : null}
            {saveState.error ? <div className="shell-banner">{saveState.error}</div> : null}

            {moduleState.loading ? (
                <section className="shell-section">
                    <div className="shell-emptyState shell-emptyState--compact">Загружаем данные модуля...</div>
                </section>
            ) : moduleState.item ? (
                <section className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <h3>{moduleState.item.serial_number}</h3>
                        </div>
                        <span>{moduleState.item.location?.city || '—'}</span>
                    </div>

                    <div className="shell-requestDetail">
                        <div className="shell-requestDetailGrid">
                            <div className="shell-requestDetailItem">
                                <span>Серийный номер</span>
                                <strong>{moduleState.item.serial_number || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Партийный номер</span>
                                <strong>{moduleState.item.party_number || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Система</span>
                                <strong>{moduleState.item.system || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Локация</span>
                                <strong>{moduleState.item.location?.city || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Статус ремонта</span>
                                <strong>{moduleState.item.repair_status || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Статус движения</span>
                                <strong>{moduleState.item.movement_status || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Дата поступления</span>
                                <strong>{formatDate(moduleState.item.arrival_date)}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Дата ремонта</span>
                                <strong>{formatDate(moduleState.item.date_repair)}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Дата отправки</span>
                                <strong>{formatDate(moduleState.item.date_of_sending)}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Инженер</span>
                                <strong>{moduleState.item.engineer || '—'}</strong>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>Заявка</span>
                                <div className="shell-requestDetailRequestBlock">
                                    <strong>{currentRequest?.application_name || currentRequest?.slug || '—'}</strong>
                                    {currentRequest?.slug ? (
                                        <em className="shell-requestDetailMeta">{currentRequest.slug}</em>
                                    ) : null}
                                    <SearchableSelect
                                        value={draft.request_slug}
                                        options={requestSelectOptions}
                                        onChange={(value) => {
                                            if (!value) {
                                                clearRequestBinding();
                                                return;
                                            }
                                            const option = [selectedRequest, ...requestOptions].find((item) => item?.slug === value);
                                            if (option) {
                                                selectRequest(option);
                                            }
                                        }}
                                        onSearchChange={setRequestQuery}
                                        loading={requestLookupLoading}
                                        placeholder="Начни вводить slug, название, получателя или направление"
                                        noResultsText={requestLookupError || 'По этому запросу заявки не найдены'}
                                        className="shell-requestInlineSelect"
                                        onFocus={() => {
                                            if (!requestQuery.trim() && requestOptions.length <= 1) {
                                                fetchRequestOptions('', draft.request_slug || '').catch(() => {});
                                            }
                                        }}
                                    />
                                    {requestChanged ? (
                                        <em className="shell-requestDetailMeta shell-requestDetailMeta--pending">Новая заявка выбрана. Сохрани модуль, чтобы применить привязку.</em>
                                    ) : null}
                                </div>
                            </div>
                            <div className="shell-requestDetailItem">
                                <span>ТТН</span>
                                <strong>{currentRequest?.ttn || '—'}</strong>
                            </div>
                        </div>

                        <div className="shell-requestActionLayout">
                            <div className="shell-requestActionCard">
                                <span className="shell-cardLabel">Файлы заявки</span>
                                {requestChanged ? (
                                    <div className="shell-inlineMessage">
                                        Сохрани модуль, чтобы открыть файлы новой заявки.
                                    </div>
                                ) : moduleState.item.request_send?.excel_file_url || moduleState.item.request_send?.pdf_file_url ? (
                                    <div className="shell-requestActionButtons">
                                        {moduleState.item.request_send?.excel_file_url ? (
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                onClick={() => handleDownload('excel')}
                                                disabled={downloadState.excel}
                                            >
                                                {downloadState.excel ? 'Скачиваем...' : 'Скачать акт'}
                                            </button>
                                        ) : null}
                                        {moduleState.item.request_send?.pdf_file_url ? (
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                onClick={() => handleDownload('pdf')}
                                                disabled={downloadState.pdf}
                                            >
                                                {downloadState.pdf ? 'Скачиваем...' : 'Скачать ТТН'}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="shell-emptyState shell-emptyState--compact">У выбранного модуля нет связанных файлов.</div>
                                )}
                            </div>

                            <div className="shell-requestActionCard">
                                <span className="shell-cardLabel">Комментарий</span>
                                <div className="shell-resultComment">
                                    <p>{moduleState.item.comment || 'Без комментария'}</p>
                                </div>
                            </div>
                        </div>

                        <form className="shell-searchForm shell-searchForm--embedded" onSubmit={handleSave}>
                            <label className="shell-field">
                                <span>Статус ремонта</span>
                                <SearchableSelect
                                    value={draft.repair_status}
                                    options={metadata.choices?.repair_statuses || []}
                                    onChange={(value) => updateDraft('repair_status', value)}
                                    placeholder="Статус ремонта"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Статус движения</span>
                                <SearchableSelect
                                    value={draft.movement_status}
                                    options={metadata.choices?.movement_statuses || []}
                                    onChange={(value) => updateDraft('movement_status', value)}
                                    placeholder="Статус движения"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Система</span>
                                <SearchableSelect
                                    value={draft.system}
                                    options={metadata.choices?.systems || []}
                                    onChange={(value) => updateDraft('system', value)}
                                    placeholder="Система"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Инженер</span>
                                <input value={draft.engineer} onChange={(event) => updateDraft('engineer', event.target.value)} />
                            </label>

                            <label className="shell-field">
                                <span>Дата поступления</span>
                                <input type="date" value={draft.arrival_date || ''} onChange={(event) => updateDraft('arrival_date', event.target.value)} />
                            </label>

                            <label className="shell-field">
                                <span>Дата ремонта</span>
                                <input type="date" value={draft.date_repair || ''} onChange={(event) => updateDraft('date_repair', event.target.value)} />
                            </label>

                            <label className="shell-field">
                                <span>Дата отправки</span>
                                <input type="date" value={draft.date_of_sending || ''} onChange={(event) => updateDraft('date_of_sending', event.target.value)} />
                            </label>

                            <label className="shell-field shell-field--full">
                                <span>Комментарий</span>
                                <textarea value={draft.comment} onChange={(event) => updateDraft('comment', event.target.value)} />
                            </label>

                            <div className="shell-searchActions">
                                <button type="submit" className="shell-actionButton shell-actionButton--primary" disabled={saveState.saving}>
                                    {saveState.saving ? 'Сохраняем...' : 'Сохранить модуль'}
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            ) : null}
        </div>
    );
}
